import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../api/axios';
import { useAuth } from '../context/AuthContext';
import '../styles/MyProfile.css';

const FAITH_LABELS = {
  1: 'Exploring',
  2: 'Growing',
  3: 'Committed',
  4: 'Devoted',
  5: 'On fire',
};

const DENOMINATION_LABELS = {
  catholic: 'Catholic',
  orthodox: 'Orthodox',
  protestant: 'Protestant',
  baptist: 'Baptist',
  pentecostal: 'Pentecostal',
  methodist: 'Methodist',
  anglican: 'Anglican',
  presbyterian: 'Presbyterian',
  lutheran: 'Lutheran',
  non_denominational: 'Non-denominational',
  other: 'Other',
};

export default function MyProfilePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [activePhotoIndex, setActivePhotoIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchMyProfile();
  }, []);

  const fetchMyProfile = async () => {
    setLoading(true);
    try {
      const res = await API.get('/api/profile/me');
      const data = res.data.profile || res.data;
      setProfile(data);
      setPhotos(data.photos || []);
    } catch (err) {
      console.error('Failed to load profile:', err);
      setError('Could not load profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="my-profile-page">
        <div className="loading-state">Loading your profile...</div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="my-profile-page">
        <div className="error-state">
          <p>{error || 'Profile not found'}</p>
          <button onClick={fetchMyProfile} className="retry-btn">
            Try again
          </button>
        </div>
      </div>
    );
  }

  const currentPhoto = photos[activePhotoIndex];
  const hasMultiplePhotos = photos.length > 1;

  const handlePhotoNav = (direction) => {
    if (!hasMultiplePhotos) return;
    if (direction === 'next' && activePhotoIndex < photos.length - 1) {
      setActivePhotoIndex(activePhotoIndex + 1);
    } else if (direction === 'prev' && activePhotoIndex > 0) {
      setActivePhotoIndex(activePhotoIndex - 1);
    }
  };

  return (
    <div className="my-profile-page">
      <header className="my-profile-header">
        <h1>My Profile</h1>
        <button
          className="edit-btn"
          onClick={() => navigate('/profile/edit')}
        >
          Edit
        </button>
      </header>

      {/* Photo carousel — same look as profile view */}
      <div className="photo-section">
        {currentPhoto ? (
          <div className="photo-frame">
            <img src={currentPhoto.url} alt={profile.full_name} />
            {!currentPhoto.is_approved && (
              <div className="pending-overlay">
                <span className="pending-badge">⏳ Pending review</span>
              </div>
            )}

            {/* Photo progress dots */}
            {hasMultiplePhotos && (
              <div className="photo-dots">
                {photos.map((_, i) => (
                  <span
                    key={i}
                    className={`photo-dot ${i === activePhotoIndex ? 'active' : ''}`}
                  />
                ))}
              </div>
            )}

            {/* Tap zones */}
            {hasMultiplePhotos && (
              <>
                <div
                  className="photo-nav-zone left"
                  onClick={() => handlePhotoNav('prev')}
                />
                <div
                  className="photo-nav-zone right"
                  onClick={() => handlePhotoNav('next')}
                />
              </>
            )}
          </div>
        ) : (
          <div className="photo-frame empty">
            <div className="empty-photo">
              <span>📷</span>
              <p>No photos yet</p>
              <button
                className="add-photo-btn"
                onClick={() => navigate('/profile/edit')}
              >
                Add your first photo
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Profile completion progress */}
      <ProfileCompleteness profile={profile} photos={photos} />

      {/* Identity */}
      <section className="info-section">
        <div className="name-row">
          <h2>{profile.full_name}</h2>
          {profile.age != null && <span className="age">{profile.age}</span>}
        </div>

        {profile.location && (
          <p className="location">📍 {profile.location}</p>
        )}
      </section>

      {/* Faith */}
      {(profile.denomination || profile.faith_level || profile.home_church) && (
        <section className="info-section faith-section">
          <h3>Faith</h3>
          {profile.denomination && (
            <div className="info-row">
              <span className="info-label">Denomination</span>
              <span className="info-value">
                {DENOMINATION_LABELS[profile.denomination] || profile.denomination}
              </span>
            </div>
          )}
          {profile.faith_level && (
            <div className="info-row">
              <span className="info-label">Faith level</span>
              <span className="info-value">
                {FAITH_LABELS[profile.faith_level] || `Level ${profile.faith_level}`}
              </span>
            </div>
          )}
          {profile.home_church && (
            <div className="info-row">
              <span className="info-label">Home church</span>
              <span className="info-value">{profile.home_church}</span>
            </div>
          )}
        </section>
      )}

      {/* Bio */}
      {profile.bio && (
        <section className="info-section">
          <h3>About me</h3>
          <p className="bio-text">{profile.bio}</p>
        </section>
      )}

      {/* Looking for */}
      {profile.looking_for && (
        <section className="info-section">
          <h3>Looking for</h3>
          <p className="bio-text">{profile.looking_for}</p>
        </section>
      )}

      {/* Quick actions */}
      <section className="actions-section">
        <button
          className="action-btn"
          onClick={() => navigate('/profile/edit')}
        >
          ✏️  Edit profile
        </button>
        <button
          className="action-btn"
          onClick={() => navigate('/settings')}
        >
          ⚙️  Settings
        </button>
        <button
          className="action-btn premium-btn"
          onClick={() => navigate('/premium')}
        >
          ✨  Upgrade to Godly+
        </button>
      </section>

      {/* Account email at the bottom (low key — not a section header) */}
      <p className="account-email">
        Signed in as {user?.email}
      </p>
    </div>
  );
}

// Profile completeness progress bar — encourages filling in missing fields.
function ProfileCompleteness({ profile, photos }) {
  const checks = [
    { label: 'Profile photo', done: photos.length > 0 },
    { label: '3+ photos', done: photos.length >= 3 },
    { label: 'Bio', done: !!profile.bio?.trim() },
    { label: 'Denomination', done: !!profile.denomination },
    { label: 'Faith level', done: !!profile.faith_level },
    { label: 'Home church', done: !!profile.home_church?.trim() },
    { label: 'Looking for', done: !!profile.looking_for?.trim() },
  ];

  const completed = checks.filter(c => c.done).length;
  const total = checks.length;
  const percentage = Math.round((completed / total) * 100);

  if (percentage === 100) return null;

  return (
    <section className="completeness-section">
      <div className="completeness-header">
        <span>Profile {percentage}% complete</span>
        <span className="completeness-count">{completed}/{total}</span>
      </div>
      <div className="progress-bar">
        <div
          className="progress-fill"
          style={{ width: `${percentage}%` }}
        />
      </div>
      <div className="missing-items">
        {checks.filter(c => !c.done).map((c, i) => (
          <span key={i} className="missing-item">
            ○ {c.label}
          </span>
        ))}
      </div>
    </section>
  );
}